<template>
  <!-- Toast 방식 저장 상태 표시 -->
  <Teleport to="body">
    <Transition
      name="save-toast"
      enter-active-class="save-toast-enter-active"
      leave-active-class="save-toast-leave-active"
      enter-from-class="save-toast-enter-from"
      leave-to-class="save-toast-leave-to"
    >
      <div v-if="showToastFlag" class="save-toast" :class="toastStatus.class" role="alert" aria-live="polite">
        <div class="save-toast-icon">
          <div v-if="toastStatus.type === 'saving'" class="loading-spinner"></div>
          <svg v-else-if="toastStatus.type === 'saved'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <svg v-else-if="toastStatus.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <svg
            v-else-if="toastStatus.type === 'warning'"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div class="save-toast-content">
          <div class="save-toast-message">{{ toastStatus.message }}</div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <UiModal v-model="isOpen" :show-footer="true" :style="{ '--popup-width': '640px' }" @update:model-value="handleClose">
    <template #title>
      <div class="flex items-center gap-8">
        <span>{{ modalTitle }}</span>
        <span :class="['actual-modal__type-badge', isQualitative ? 'actual-modal__type-badge--qualitative' : '']">
          {{ isQualitative ? t('performance.kpi.goalTable.qualitative') : t('performance.kpi.goalTable.quantitative') }}
        </span>
      </div>
    </template>

    <div class="actual-modal">
      <!-- ============================================ -->
      <!-- 공통: KPI명 -->
      <!-- ============================================ -->
      <div class="actual-modal__field actual-modal__field--full">
        <label class="actual-modal__label">{{ t('performance.kpi.form.kpiName') }}</label>
        <UiInput :model-value="selectedKpi?.kpiNm || ''" size="small" readonly />
      </div>

      <!-- ============================================ -->
      <!-- 계량: 연목표 (한줄) -->
      <!-- ============================================ -->
      <div v-if="!isQualitative" class="actual-modal__field actual-modal__field--full">
        <label class="actual-modal__label">{{ t('performance.kpi.form.annualTarget') }}</label>
        <UiInput :model-value="formatValueWithUnit(selectedKpi?.targetStr) || ''" size="small" readonly />
      </div>

      <!-- ============================================ -->
      <!-- 비계량: 년목표 (여러줄 textarea) -->
      <!-- ============================================ -->
      <div v-else class="actual-modal__field actual-modal__field--full">
        <label class="actual-modal__label">{{ t('performance.kpi.actualAddModal.yearTarget') }}</label>
        <UiTextarea :model-value="selectedKpi?.plan || ''" size="small" :rows="3" readonly />
      </div>

      <div class="actual-modal__grid">
        <!-- 실적월 -->
        <div class="actual-modal__field">
          <label class="actual-modal__label">
            {{ t('performance.kpi.actualAddModal.actualMonth') }}
            <span class="actual-modal__required">*</span>
          </label>
          <UiSelect v-model="form.actualMon" :options="monthOptions" :placeholder="t('performance.kpi.actualAddModal.selectMonth')" />
        </div>

        <!-- ============================================ -->
        <!-- 계량 케이스 (kpiTypeCd: '01') -->
        <!-- ============================================ -->
        <template v-if="!isQualitative">
          <!-- 실적 (숫자) + 누적 표시 -->
          <div class="actual-modal__field" style="position: relative">
            <label class="actual-modal__label">
              {{ t('performance.kpi.actualTab.actual') }}
              <span class="actual-modal__required">*</span>
            </label>
            <div class="actual-modal__input-with-label">
              <UiInput
                v-model="form.actualVal"
                type="text"
                unsigned
                data-field="actualVal"
                :placeholder="t('performance.kpi.actualAddModal.phActualValue')"
                size="small"
                required
                @input="
                  e => {
                    let v = e.target.value.replace(/[^0-9.]/g, '');
                    if ((v.match(/\./g) || []).length > 1) v = v.replace(/\.(?=.*\.)/g, '');
                    const parts = v.split('.');
                    if (parts[0].length > 15) parts[0] = parts[0].slice(0, 15);
                    if (parts[1] !== undefined && parts[1].length > 5) parts[1] = parts[1].slice(0, 5);
                    v = parts[1] !== undefined ? parts[0] + '.' + parts[1] : parts[0];
                    e.target.value = v;
                    form.actualVal = v;
                  }
                "
              />
              <span v-if="targetUnit" class="actual-modal__unit">{{ targetUnit }}</span>
            </div>
            <span class="actual-modal__accumulated" style="position: absolute; bottom: -22px; right: 19px">
              {{ t('performance.kpi.actualAddModal.accumulated', { value: formatNumber(accumulatedActual), unit: targetUnit }) }}
            </span>
          </div>

          <!-- 누적 달성률 -->
          <div class="actual-modal__field">
            <label class="actual-modal__label">{{ t('performance.kpi.actualAddModal.cumulativeAchievement') }}</label>
            <UiInput v-if="selectedKpi?.rateMethodCd === '002'" v-model="manualAccrueAchieve" size="small" suffix="%" type="number" />
            <UiInput v-else :model-value="formatRate(accumulatedRate) + ' %'" size="small" readonly />
          </div>
          <!-- 진행상태 -->
          <div class="actual-modal__field">
            <label class="actual-modal__label">
              {{ t('performance.kpi.actualAddModal.progressStatus') }}
              <span class="actual-modal__required">*</span>
            </label>
            <UiSelect v-model="form.status" :options="statusOptions" :placeholder="t('performance.kpi.actualAddModal.selectStatus')" />
          </div>
        </template>

        <!-- ============================================ -->
        <!-- 비계량 케이스 (kpiTypeCd: '02') -->
        <!-- ============================================ -->
        <template v-else>
          <!-- 진척률 -->
          <div class="actual-modal__field">
            <label class="actual-modal__label">
              {{ t('performance.kpi.actualAddModal.progressPercent') }}
              <span class="actual-modal__required">*</span>
            </label>
            <UiInput
              v-model="form.progress"
              type="text"
              data-field="progress"
              :placeholder="t('performance.kpi.actualAddModal.phProgress')"
              size="small"
              maxlength="6"
              required
              @input="
                e => {
                  // 진척률(%): 정수 3자리 + 소수 2자리 (예: 100.99)
                  let v = e.target.value.replace(/[^0-9.]/g, '');
                  if ((v.match(/\./g) || []).length > 1) v = v.replace(/\.(?=.*\.)/g, '');
                  const parts = v.split('.');
                  if (parts[0].length > 3) parts[0] = parts[0].slice(0, 3);
                  if (parts[1] !== undefined && parts[1].length > 2) parts[1] = parts[1].slice(0, 2);
                  v = parts[1] !== undefined ? parts[0] + '.' + parts[1] : parts[0];
                  e.target.value = v;
                  form.progress = v;
                }
              "
            />
          </div>

          <!-- 진행상태 -->
          <div class="actual-modal__field">
            <label class="actual-modal__label">
              {{ t('performance.kpi.actualAddModal.progressStatus') }}
              <span class="actual-modal__required">*</span>
            </label>
            <UiSelect v-model="form.status" :options="statusOptions" :placeholder="t('performance.kpi.actualAddModal.selectStatus')" />
          </div>

          <!-- 빈 칸 (그리드 정렬용) -->
          <div class="actual-modal__field actual-modal__field--empty"></div>
        </template>
      </div>

      <!-- ============================================ -->
      <!-- 공통: 실행내용 -->
      <!-- ============================================ -->
      <div class="actual-modal__field actual-modal__field--full">
        <label class="actual-modal__label">
          {{ t('performance.kpi.actualAddModal.fieldExecutionContent') }}
          <span class="actual-modal__required">*</span>
        </label>
        <UiTextarea
          v-model="form.actualDesc"
          data-field="actualDesc"
          :placeholder="
            isQualitative
              ? t('performance.kpi.actualAddModal.phExecutionQualitative')
              : t('performance.kpi.actualAddModal.phExecutionQuantitative')
          "
          :rows="4"
          :maxlength="2000"
          size="small"
          required
        />
      </div>

      <!-- ============================================ -->
      <!-- 공통: 첨부파일 -->
      <!-- ============================================ -->
      <div class="actual-modal__field actual-modal__field--full">
        <UiFileAttachment v-model="form.files" :title="t('performance.kpi.actualTab.attachments')" />
      </div>
    </div>

    <!-- Footer -->
    <template #footerActions>
      <UiButton variant="secondary-line" @click="handleClose">{{ t('common.action.cancel') }}</UiButton>
      <UiButton variant="primary" :disabled="isSaving" @click="handleSave">
        {{ isSaving ? t('performance.kpi.actualAddModal.saving') : t('common.action.save') }}
      </UiButton>
    </template>
  </UiModal>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useKpiStore } from '~/composables/performance/kpi/useKpiStore';
import { useKpiDetailStore } from '~/composables/performance/kpi/useKpiDetailStore';
import { useKpiI18n, kpiT } from '~/composables/performance/kpi/useKpiI18n';
import { useAttachmentApi } from '~/composables/attachment/useAttachmentApi';
import { useAttachmentStore } from '~/composables/attachment/useAttachmentStore';
import UiFileAttachment from '~/components/attachment/UiFileAttachment.vue';

const { t, actualMonthLabel } = useKpiI18n();

// 첨부파일 API & Store
const { fetchAtchFileList } = useAttachmentApi();
const { deleteFileIds } = useAttachmentStore();
const manualAccrueAchieve = ref('');

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  editRecord: {
    type: Object,
    default: null,
  },
});

// Emits
const emit = defineEmits(['update:modelValue', 'saved']);

// 메인 Store: 선택된 KPI, 상태 목록
const { selectedKpi, kpiStatusList, agreementYn, agreementData } = useKpiStore();

// 실적상세 Store: 월별 실적 CRUD
const { monthlyRecords, saveActual, deleteActual, fetchMonthlyRecords } = useKpiDetailStore();

// ============================================
// 계량/비계량 판별
// ============================================
const isQualitative = computed(() => selectedKpi.value?.kpiTypeCd === '02');

// 모달 타이틀
const modalTitle = computed(() =>
  isEditMode.value ? t('performance.kpi.actualAddModal.titleEdit') : t('performance.kpi.actualAddModal.titleAdd'),
);

// ============================================
// Toast 상태 관리
// ============================================
const showToastFlag = ref(false);
const toastStatus = ref({
  type: 'saved',
  message: '',
  class: 'save-toast--saved',
});
let toastTimeout = null;

/**
 * Toast 표시 함수
 */
const showToast = (type, message, duration = 3000) => {
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }

  toastStatus.value = {
    type,
    message,
    class: `save-toast--${type}`,
  };

  showToastFlag.value = true;

  // 자동 숨김 (saving이 아닐 때만)
  if (type !== 'saving' && duration > 0) {
    toastTimeout = setTimeout(() => {
      showToastFlag.value = false;
    }, duration);
  }
};

// ============================================
// 폼 상태
// ============================================

// 수정 모드 여부
const isEditMode = computed(() => !!props.editRecord);

// 저장 중 상태
const isSaving = ref(false);

// 모달 열림 상태
const isOpen = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
});

// 폼 데이터
const form = ref({
  actualMon: '',
  // 계량 필드
  actualVal: '',
  // 비계량 필드
  progress: '',
  // 공통 필드
  status: '',
  actualDesc: '',
  files: [],
  atchFileId: '',
});

// 수정 모드에서 원래 월 저장 (월 변경 시 기존 레코드 삭제용)
const originalMon = ref('');

// 월 옵션 (이미 등록된 월 제외, 수정 모드에서는 현재 월 포함)
// value를 "01", "02" 형식의 문자열로 통일 (백엔드 ON DUPLICATE KEY UPDATE 조건과 일치)
const monthOptions = computed(() => {
  const existingMonths = monthlyRecords.value.map(r => r.actualMon);

  const allMonths = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const value = String(month).padStart(2, '0');
    return {
      label: actualMonthLabel(value),
      value,
    };
  });

  const editingMonth = props.editRecord?.actualMon;
  return allMonths.filter(m => !existingMonths.includes(m.value) || m.value === editingMonth);
});

// 상태 옵션 (시스템 설정에서 조회한 목록 사용)
const statusOptions = computed(() => {
  if (kpiStatusList.value && kpiStatusList.value.length > 0) {
    return kpiStatusList.value.map(item => ({
      label: item.statusNm,
      value: item.statusCd,
      color: item.color,
    }));
  }
  // fallback (API 응답 전 또는 오류 시) - AS-IS 기준
  return [
    { label: t('performance.kpi.actualAddModal.statusExcellent'), value: 'S001', color: '#3788e4' },
    { label: t('performance.kpi.actualAddModal.statusNormal'), value: 'S002', color: '#fec907' },
    { label: t('performance.kpi.actualAddModal.statusPoor'), value: 'S003', color: '#e02a10' },
  ];
});

// 목표값에서 숫자만 추출 (예: "10명" → 10)
const targetValue = computed(() => {
  if (!selectedKpi.value?.targetStr) return 0;
  const match = String(selectedKpi.value.targetStr).match(/^([\d,.]+)/);
  if (!match) return 0;
  return Number(match[1].replace(/,/g, '')) || 0;
});

// 목표값에서 단위 추출 (예: "10명" → "명")
const targetUnit = computed(() => {
  if (!selectedKpi.value?.targetStr) return '';
  const match = String(selectedKpi.value.targetStr).match(/^[\d,.]+(.*)$/);
  return match ? match[1].trim() : '';
});

// 실적이 있는 월 목록 (현재 월 포함)
const recordsWithActual = computed(() => {
  const currentMon = form.value.actualMon;
  const currentActual = parseFloat(form.value.actualVal) || 0;

  // 기존 월별 실적 (현재 월 제외) + 현재 입력값
  const others = monthlyRecords.value.filter(r => r.actualMon !== currentMon).map(r => Number(r.actualVal) || 0);

  return [...others, currentActual];
});

// 누적실적 자동계산 (accrueMethodCd에 따라 합산/평균/당월)
const accumulatedActual = computed(() => {
  const values = recordsWithActual.value;
  const currentActual = parseFloat(form.value.actualVal) || 0;
  const method = selectedKpi.value?.accrueMethodCd || '001';

  if (method === '003') {
    // 당월: 현재 월 실적만
    return currentActual;
  }

  const total = values.reduce((sum, v) => sum + v, 0);

  if (method === '002') {
    // 평균: 등록된 월 수로 나누기
    const count = values.length || 1;
    return parseFloat((total / count).toFixed(2));
  }

  // 001 합산 (기본값)
  return total;
});

// 누적달성률 자동계산 (누적실적 / 목표 * 100)
const accumulatedRate = computed(() => {
  if (!targetValue.value) return 0;
  return ((accumulatedActual.value / targetValue.value) * 100).toFixed(2);
});

// 숫자 포맷 (천단위 콤마, 입력 자릿수 한도 내 소수점 보존)
// - 입력값(문자열)은 정밀도 그대로 보존
// - 계산된 누적값은 maxDecimals(5) 가드로 IEEE 754 부동소수점 노이즈 차단
const formatNumber = (value, maxDecimals = 5) =>
  formatNumberDisplay(value, { maxDecimals, empty: '0' });

// 퍼센트 포맷 (불필요한 소수점 제거)
const formatRate = value => {
  if (value === null || value === undefined || value === '') return '0';
  const num = Number(value);
  if (isNaN(num)) return value;
  // 정수면 소수점 없이, 소수점 있으면 불필요한 0 제거
  return num % 1 === 0 ? num.toString() : num.toFixed(2).replace(/\.?0+$/, '');
};

// 단위 포함 문자열 포맷 (예: "25.00명" → "25명")
const formatValueWithUnit = str => {
  if (!str || str === '-') return str;
  // 숫자와 단위 분리 (예: "25.00명" → ["25.00", "명"])
  const match = String(str).match(/^([\d,.]+)(.*)$/);
  if (!match) return str;
  const [, numPart, unit] = match;
  // 콤마 제거 후 문자열 그대로 넘겨 정밀도 보존
  const cleaned = numPart.replace(/,/g, '');
  if (isNaN(Number(cleaned))) return str;
  return formatNumber(cleaned) + unit.trim();
};

// ============================================
// 핸들러
// ============================================

// 모달 닫기
const handleClose = () => {
  emit('update:modelValue', false);
  resetForm();
};

// 폼 초기화
const resetForm = () => {
  form.value = {
    actualMon: '',
    actualVal: '',
    progress: '',
    status: '',
    actualDesc: '',
    files: [],
    atchFileId: '',
  };
  isSaving.value = false;
  // 삭제 대상 파일 목록 초기화
  deleteFileIds.value = [];
};

// 첨부파일 검증 설정 (백엔드 fileUpload.* globals.properties와 동일)
const fileValidationConfig = {
  maxFileCnt: 5,
  maxFileSize: 10000000, // 10MB - 개별 파일 한도
  maxTotalSize: 52428800, // 50MB - 총 파일 크기 합 한도 (AS-IS defaultMaxSize)
  allowFileExts: [
    'jpg',
    'jpeg',
    'gif',
    'png',
    'bmp',
    'txt',
    'pdf',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'doc',
    'docx',
    'hwp',
    'hwpx',
    'heic',
    'zip',
    '7z',
    'rar',
    'alz',
    'avi',
    'mp4',
  ],
};

// 파일 확장자 추출
const getFileExtension = filename => {
  if (!filename) return '';
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

/**
 * 유효성 검사
 * @returns {boolean} 유효성 통과 여부
 */
const validateForm = () => {
  // 포커스 헬퍼
  const focusField = (fieldKey) => {
    nextTick(() => {
      const el = document.querySelector(`.actual-modal [data-field="${fieldKey}"]`);
      el?.focus();
    });
  };

  // 합의 기능 사용 중이고, 합의 완료(003)가 아닌 경우 저장 차단
  if (agreementYn.value && agreementData.value?.agreementCd !== '003') {
    showToast('warning', t('performance.kpi.toast.actualAgreementBlocked'));
    return false;
  }

  // 실적월
  if (!form.value.actualMon) {
    showToast('warning', t('performance.kpi.toast.actualMonthRequired'));
    return false;
  }

  const labelActual = t('performance.kpi.actualAddModal.fieldActual');
  const labelProgress = t('performance.kpi.actualAddModal.fieldProgress');

  // 계량 필수값 (실적)
  if (!isQualitative.value) {
    if (!form.value.actualVal) {
      showToast('warning', t('performance.kpi.toast.actualValueRequired'));
      focusField('actualVal');
      return false;
    }
    const actualStr = String(form.value.actualVal);
    const [intPart = '', fracPart = ''] = actualStr.split('.');
    if (intPart.length > 15) {
      showToast('warning', t('performance.kpi.toast.fieldDigitsIntegerMax', { label: labelActual, max: 15 }));
      focusField('actualVal');
      return false;
    }
    if (fracPart.length > 5) {
      showToast('warning', t('performance.kpi.toast.fieldDigitsFractionMax', { label: labelActual, max: 5 }));
      focusField('actualVal');
      return false;
    }
  }

  // 비계량 필수값 (진척률)
  if (isQualitative.value) {
    if (!form.value.progress) {
      showToast('warning', t('performance.kpi.toast.progressRateRequired'));
      focusField('progress');
      return false;
    }
    const progressStr = String(form.value.progress);
    const [progIntPart = '', progFracPart = ''] = progressStr.split('.');
    if (progIntPart.length > 3) {
      showToast('warning', t('performance.kpi.toast.fieldDigitsIntegerMax', { label: labelProgress, max: 3 }));
      focusField('progress');
      return false;
    }
    if (progFracPart.length > 2) {
      showToast('warning', t('performance.kpi.toast.fieldDigitsFractionMax', { label: labelProgress, max: 2 }));
      focusField('progress');
      return false;
    }
  }

  // 진행상태
  if (!form.value.status) {
    showToast('warning', t('performance.kpi.toast.progressStatusRequired'));
    return false;
  }

  // 실행내용 필수값
  if (!form.value.actualDesc?.trim()) {
    showToast('warning', t('performance.kpi.toast.actualDescRequired'));
    focusField('actualDesc');
    return false;
  }
  if (form.value.actualDesc.length > 2000) {
    showToast('warning', t('performance.kpi.toast.actualDescMaxLength'));
    focusField('actualDesc');
    return false;
  }

  // 첨부파일 검증 (신규 파일만 검사)
  const newFiles = form.value.files.filter(f => f instanceof File);
  if (newFiles.length > 0) {
    const invalidFiles = newFiles.filter(file => {
      const ext = getFileExtension(file.name);
      return !fileValidationConfig.allowFileExts.includes(ext);
    });
    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map(f => f.name).join(', ');
      showToast('warning', t('performance.kpi.toast.invalidFileType', { names: invalidNames }));
      return false;
    }

    const oversizedFiles = newFiles.filter(file => file.size > fileValidationConfig.maxFileSize);
    if (oversizedFiles.length > 0) {
      const oversizedNames = oversizedFiles.map(f => f.name).join(', ');
      showToast('warning', t('performance.kpi.toast.fileSizeExceeded10', { names: oversizedNames }));
      return false;
    }

    if (form.value.files.length > fileValidationConfig.maxFileCnt) {
      showToast('warning', t('performance.kpi.toast.maxFileUpload', { count: fileValidationConfig.maxFileCnt }));
      return false;
    }

    const totalSize = form.value.files.reduce((sum, f) => {
      if (f instanceof File) return sum + f.size;
      if (typeof f.fileSize === 'number') return sum + f.fileSize;
      return sum;
    }, 0);
    if (totalSize > fileValidationConfig.maxTotalSize) {
      const maxMB = Math.floor(fileValidationConfig.maxTotalSize / 1024 / 1024);
      showToast('warning', t('performance.kpi.toast.totalFileSizeMax', { max: maxMB }));
      return false;
    }
  }

  return true;
};

/**
 * 저장 처리
 */
const handleSave = async () => {
  // 유효성 검사
  if (!validateForm()) return;

  await executeSave();
};

/**
 * 실제 저장 실행
 */
const executeSave = async () => {
  // 저장 중 상태
  isSaving.value = true;
  showToast('saving', t('performance.kpi.actualAddModal.saving'));

  try {
    // 백엔드 API에 맞는 데이터 구성 (레거시와 동일하게)
    const kpiTypeCd = selectedKpi.value?.kpiTypeCd || '01';
    const newMon = String(form.value.actualMon).padStart(2, '0');

    // 수정 모드에서 월이 변경된 경우, 기존 레코드 삭제 필요
    const isMonthChanged = isEditMode.value && originalMon.value && originalMon.value !== newMon;

    if (isMonthChanged) {
      // 기존 월 레코드 삭제
      await deleteActual(originalMon.value);
    }

    // 공통 데이터 (레거시에서 보내는 필드들 포함)
    const saveData = {
      kpiTypeCd,
      mon: newMon, // 01, 02 형식
      description: form.value.actualDesc || '',
      statusCd: getStatusCode(form.value.status),
      // 레거시에서 보내는 추가 필드들
      accrueMethodCd: selectedKpi.value?.accrueMethodCd || '001',
      rateMethodCd: selectedKpi.value?.rateMethodCd || '001',
      sharingYn: selectedKpi.value?.sharingYn || 'N',
      // 첨부파일 (삭제 체크된 파일 제외)
      files: form.value.files.filter(f => !deleteFileIds.value.includes(f.fileSn)),
      atchFileId: form.value.atchFileId || '', // 수정 시 기존 파일ID
      deleteFileIds: deleteFileIds.value, // 삭제할 파일 ID 목록
    };

    // 타입별 데이터 분기
    if (isQualitative.value) {
      // 비계량 (02): achive에 진척률 값 사용 (백엔드에서 progress로 변환)
      saveData.achive = form.value.progress || '0';
      saveData.actual = ''; // 비계량은 actual 빈값
      saveData.accrueAchieve = ''; // 비계량은 accrueAchieve 빈값
    } else {
      // 계량 (01): actual, achive(당월달성률), accrueAchieve(누적달성률)
      const currentActual = parseFloat(form.value.actualVal) || 0;
      const currentRate = targetValue.value ? ((currentActual / targetValue.value) * 100).toFixed(2) : '0';
      saveData.actual = form.value.actualVal || '0';

      if (selectedKpi.value?.rateMethodCd === '002') {
        // 달성율 계산 방법: 입력 → 사용자가 직접 입력한 값 사용
        saveData.achive = currentRate;
        saveData.accrueAchieve = manualAccrueAchieve.value || '0';
      } else {
        // 달성율 계산 방법: 달성률 → 자동계산
        saveData.achive = currentRate;
        saveData.accrueAchieve = accumulatedRate.value?.toString() || '0';
      }
    }

    // 실제 API 호출
    const result = await saveActual(saveData);

    // 백엔드 응답 result 체크 (FAIL 시 성공 처리 스킵)
    if (result?.result !== 'SUCCESS') {
      showToast('error', result?.msg || t('performance.kpi.toast.actualSaveFailed'));
      isSaving.value = false;
      return;
    }

    // 성공 Toast
    showToast('saved', isEditMode.value ? t('performance.kpi.toast.actualUpdated') : t('performance.kpi.toast.actualRegistered'));

    // deleteFileIds 초기화
    deleteFileIds.value = [];

    // 월별 실적 목록 새로고침
    await fetchMonthlyRecords();

    // 모달 닫기
    handleClose();

    // 부모에게 알림 (모달 닫힌 후 목록 갱신)
    emit('saved', saveData);
  } catch (error) {
    console.error('실적 저장 실패:', error);
    showToast('error', t('performance.kpi.toast.actualSaveFailedRetry'));
    isSaving.value = false;
  }
};

/**
 * 상태값을 상태코드로 변환
 * - 이미 코드 형식이면 그대로 반환
 * - 기존 데이터(상태명)는 코드로 변환
 */
const getStatusCode = statusValue => {
  // S001, S002, S003 형식 (시스템 설정 코드)
  if (/^S\d{3}$/.test(statusValue)) {
    return statusValue;
  }
  // 이미 코드 형식인 경우 (01, 02 등 - 레거시)
  if (/^\d{2}$/.test(statusValue)) {
    return statusValue;
  }
  // 기존 상태명 → 코드 변환 (AS-IS 기준)
  const statusMap = {
    [kpiT('actualAddModal.statusExcellent')]: 'S001',
    [kpiT('actualAddModal.statusNormal')]: 'S002',
    [kpiT('actualAddModal.statusPoor')]: 'S003',
    우수: 'S001',
    보통: 'S002',
    미흡: 'S003',
    Excellent: 'S001',
    Average: 'S002',
    Poor: 'S003',
  };
  return statusMap[statusValue] || statusValue || '';
};

// 모달 열릴 때 폼 초기화 또는 수정 데이터 로드
// isOpen과 props.editRecord 둘 다 감시하여 수정 모드 데이터 로드 보장
watch([isOpen, () => props.editRecord], async ([newIsOpen, newEditRecord]) => {
  if (newIsOpen) {
    // 모달 열릴 때 삭제 대상 파일 목록 초기화
    deleteFileIds.value = [];
    if (newEditRecord) {
      // 수정 모드: 기존 데이터 로드
      const mon = newEditRecord.actualMon || '';

      // 입력 방식일 때 기존 누적달성률 로드
      if (selectedKpi.value?.rateMethodCd === '002') {
        manualAccrueAchieve.value = newEditRecord.accrueAchieve?.toString() || '';
      }

      // 기존 첨부파일 목록 조회
      let existingFiles = [];
      if (newEditRecord.atchFileId) {
        try {
          const fileResponse = await fetchAtchFileList({ atchFileId: newEditRecord.atchFileId });
          if (fileResponse?.fileList) {
            existingFiles = fileResponse.fileList;
          }
        } catch (error) {
          console.error('첨부파일 조회 실패:', error);
        }
      }

      form.value = {
        actualMon: mon,
        // 계량 필드
        actualVal: newEditRecord.actualVal?.toString() || '',
        // 비계량 필드
        progress: newEditRecord.progress?.toString() || '',
        // 공통 필드
        status: newEditRecord.status || '',
        actualDesc: newEditRecord.actualDesc || '',
        files: existingFiles,
        atchFileId: newEditRecord.atchFileId || '',
      };
      // 수정 모드: 원래 월 저장 (월 변경 시 기존 레코드 삭제용)
      originalMon.value = mon;
    } else {
      resetForm();
      originalMon.value = '';
      manualAccrueAchieve.value = '';
    }
  }
});
</script>
